import React, { useMemo, useEffect } from 'react';
import { BarChart } from '@/components/ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV, normalizeCell } from '../../utils/exportToCSV';
import { getFilterKeyFromColumnHeader, processDashboardUpdate } from '../../utils/processDashboardUpdate';
import { useChartColors } from '../../hooks/useChartColors';

/**
 * Bar Widget Component
 * Modernized replacement for BarList.js
 * Displays data as bar chart or table
 *
 * API field mapping (matches dev-v3 BarList.js fetchData_locally):
 *   - columnDataSet1[0] → primary series (labels, data, toplabel)
 *   - columnDataSet2[0] → optional second series
 *   - listDataSet       → table rows (array of {datasets:[{label,data}]})
 */
function BarWidget({ data, handlechangea, onLegendUpdate, onExportReady, onDataReady, mode = 'chart' }) {
    const colors = useChartColors();

    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    // API response field mapping (from old BarList.js):
    //   - Standard bar charts  → columnDataSet[0]  (total transactions, group number, patient savings, etc.)
    //   - Commission bar chart → columnDataSet1[0] + optional columnDataSet2[0]
    // Try columnDataSet1 first (commission widgets), fall back to columnDataSet (standard widgets)
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const ds1 = apiData[0].columnDataSet1?.[0] ?? apiData[0].columnDataSet?.[0];
            if (!ds1 || !ds1.labels || !ds1.data || ds1.labels.length === 0 || ds1.data.length === 0) return null;

            const datasets = [{
                label: ds1.toplabel || data.YlabelString || 'Value',
                data: ds1.data,
                backgroundColor: data.ColorCode || colors.chart1,
                borderColor: data.ColorCode || colors.chart1,
                borderWidth: 1,
                hoverBackgroundColor: colors.chart2
            }];

            // Optional second series from columnDataSet2 (commission widgets only)
            const ds2 = apiData[0].columnDataSet2?.[0];
            if (ds2 && ds2.data && ds2.data.length > 0) {
                datasets.push({
                    label: ds2.toplabel || 'Series 2',
                    data: ds2.data,
                    backgroundColor: colors.chart5,
                    borderColor: colors.chart5,
                    borderWidth: 1,
                    hoverBackgroundColor: colors.chart3
                });
            }

            return {
                labels: ds1.labels,
                datasets
            };
        } catch (err) {
            console.error('Error transforming chart data:', err);
            return null;
        }
    }, [apiData, data, colors]);

    // Transform API data to table format using listDataSet (same structure as TableWidget)
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) return { tableData: [], columns: [] };

        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return { tableData: [], columns: [] };

            // First row defines column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Column ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(item => {
                const row = {};
                (item.datasets || []).forEach((ds, idx) => {
                    row[`col${idx}`] = ds.data;
                });
                return row;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error transforming table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived chartData/tableData — the raw apiData can be non-empty yet still contain
    // no renderable data (e.g. all nested dataset arrays are empty).
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(chartData !== null || tableData.length > 0);
    }, [loading, chartData, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Report legend value to parent (for CardTitle number display, matching old BarList.js)
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            let legendVal = '';
            // Commission widgets use sumOfLegendValues, others use legend[0]
            if (data.title === 'Total Commission Earned' || data.title === 'Total Revenue Earned') {
                legendVal = apiData[0].sumOfLegendValues?.[0]?.sumOfLegendValue ?? '';
                if (legendVal) legendVal = '$' + legendVal;
            } else {
                legendVal = apiData[0].legend?.[0]?.legendValue ?? '';
            }
            if (legendVal) onLegendUpdate(data.id, String(legendVal));
        } catch (e) { /* ignore */ }
    }, [apiData]);

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [tableData]);

    // Handle row click for drill-down (single filter key = whole row is clickable).
    const handleRowClick = (row) => {
        if (handlechangea && data.DataType && data.DataType !== 'NA') {
            processDashboardUpdate(data.DataType, row.col0, () => handlechangea && handlechangea());
        }
    };

    // Handle per-cell click for widgets where different columns map to different filter keys
    // (e.g. "Transactions by Group Number" has both Group Number and Distributor Name columns).
    const handleCellClick = (row, columnKey, columnLabel) => {
        if (!handlechangea) return;
        const filterKey = getFilterKeyFromColumnHeader(columnLabel);
        if (!filterKey) return;
        const cellValue = row[columnKey];
        if (!cellValue || String(cellValue) === 'Total') return;
        processDashboardUpdate(filterKey, String(cellValue), () => handlechangea && handlechangea());
    };

    // Use per-cell filtering for widgets that have multiple distinct filterable columns.
    // For "Transactions by Group Number": Group Number → GroupNumber, Distributor Name → DistributorName,
    // # of Transactions → not filterable (returns null from getFilterKeyFromColumnHeader).
    const usePerCellFilter = data.DataType === 'GroupNumber';

    // Widgets whose column 0 is a date — space-prefix it unconditionally so all
    // spreadsheet apps (Excel, LibreOffice, Google Sheets) treat it as plain text.
    // Matches Bridge_API_Reference_Procedure BarList.js ExportWidget behaviour.
    const DATE_COL0_WIDGETS = new Set([
        'Total Patient Savings ($)',
        'Total Transactions',
        'Average Patient Savings ($)',
        'Average Patient Savings (%)',
        'Compensable Transactions',
        'Paid Transactions',
    ]);

    // Export handler
    const handleExport = () => {
        if (!apiData || apiData.length === 0) return;
        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return;

            const hasDateCol0 = DATE_COL0_WIDGETS.has(data.title);

            const tableRows = listDataSet.map((row, rowIdx) =>
                (row.datasets || []).map((ds, colIdx) => {
                    // For known date-column widgets, unconditionally prefix col 0
                    // on data rows with a space to force text mode in spreadsheets.
                    if (hasDateCol0 && rowIdx > 0 && colIdx === 0) {
                        return ' ' + String(ds.data ?? '').replace(/,/g, '');
                    }
                    return normalizeCell(ds.data);
                }).join(',')
            );

            const csvContent = tableRows.join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${data.title || 'table-data'}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting BarWidget data:', err);
        }
    };

    // Clean minimal look (no grid, no axis lines, no legend, no date ticks) for these widgets
    const CLEAN_STYLE_WIDGETS = new Set([
        'Total Transactions',
        'Transactions by Group Number',
        'Total Patient Savings ($)',
        'Transactions by Drug',
        'Total Revenue Earned',
        "Unique Member ID's",
        'Transactions by City',
        'Transactions by State',
    ]);
    const isTotalTransactions = CLEAN_STYLE_WIDGETS.has(data.title);


    // Whether clicking a bar fires a drill-down filter
    const isClickable = !!(data.DataType && data.DataType !== 'NA');

    // Chart options
    const chartOptions = {
        onClick: (event, elements) => {
            if (elements.length > 0 && chartData && isClickable) {
                const index = elements[0].index;
                const label = chartData.labels[index];
                // Write filter tag to localStorage then trigger dashboard refresh (matches old BarList.js)
                processDashboardUpdate(data.DataType, label, () => handlechangea && handlechangea());
            }
        },
        // Show pointer cursor when hovering over a clickable bar
        onHover: isClickable
            ? (event, elements) => {
                if (event?.native?.target) {
                    event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                }
            }
            : undefined,
        plugins: {
            legend: {
                display: !isTotalTransactions
            }
        },
        scales: {
            x: {
                title: {
                    display: !!data.XlabelString,
                    text: data.XlabelString || ''
                },
                ticks: {
                    display: !isTotalTransactions
                },
                grid: {
                    display: !isTotalTransactions
                },
                border: {
                    display: !isTotalTransactions
                }
            },
            y: {
                title: {
                    display: !!data.YlabelString,
                    text: data.YlabelString || ''
                },
                beginAtZero: true,
                grid: {
                    display: !isTotalTransactions
                },
                border: {
                    display: !isTotalTransactions
                }
            }
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <BarChart
                        data={chartData}
                        options={chartOptions}
                        height={280}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        onRowClick={(!usePerCellFilter && isClickable) ? handleRowClick : null}
                        onCellClick={usePerCellFilter ? handleCellClick : null}
                        pagination={{ enabled: true, itemsPerPage: 6 }}
                        sortable={true}
                        searchable={false}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            mode={mode}
            chartAlign={chartData !== null ? 'bottom' : 'center'}
        />
    );
}

export default BarWidget;
